import { Check, CircleDashed, Clock, Pause, X } from 'lucide-react';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';

import { cn } from '../../../lib/cn';
import type { TaskRow, TaskStatus } from './tasks.schema';
import { TASK_STATUSES } from './tasks.schema';
import styles from './tasks.module.css';

const STATUS_LABELS: Record<TaskStatus, string> = {
  todo: 'לעשות',
  in_progress: 'בעבודה',
  blocked: 'חסום',
  done: 'בוצע',
  cancelled: 'בוטל',
};

const STATUS_ICON: Record<TaskStatus, typeof Check> = {
  todo: CircleDashed,
  in_progress: Clock,
  blocked: Pause,
  done: Check,
  cancelled: X,
};

const STATUS_CLASS: Record<TaskStatus, string> = {
  todo: 'statusTodo',
  in_progress: 'statusInProgress',
  blocked: 'statusBlocked',
  done: 'statusDone',
  cancelled: 'statusCancelled',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return format(new Date(iso), 'dd/MM/yy', { locale: he });
  } catch {
    return iso;
  }
}

function isOverdue(dueDate: string | null, status: TaskStatus): boolean {
  if (!dueDate || status === 'done' || status === 'cancelled') return false;
  try {
    const due = new Date(dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return due < today;
  } catch {
    return false;
  }
}

interface TaskListProps {
  tasks: TaskRow[];
  projectNameById: Record<string, string>;
  onSelect: (task: TaskRow) => void;
  onChangeStatus: (id: string, status: TaskStatus) => void;
}

export function TaskList({ tasks, projectNameById, onSelect, onChangeStatus }: TaskListProps) {
  return (
    <div className={styles.table} role="table" aria-label="משימות">
      <div className={cn(styles.rowGrid, styles.head)} role="row">
        <div role="columnheader" />
        <div role="columnheader">משימה</div>
        <div role="columnheader">פרויקט</div>
        <div role="columnheader">סטטוס</div>
        <div role="columnheader">תאריך יעד</div>
        <div role="columnheader" />
      </div>

      {tasks.map((task) => {
        const Icon = STATUS_ICON[task.status];
        const overdue = isOverdue(task.due_date, task.status);
        const projectName = task.project_id ? (projectNameById[task.project_id] ?? '—') : 'אישית';

        return (
          <div
            key={task.id}
            className={cn(styles.rowGrid, task.status === 'done' && styles.rowDone)}
            role="row"
            tabIndex={0}
            onClick={() => {
              onSelect(task);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelect(task);
              }
            }}
          >
            <button
              type="button"
              className={cn(styles.statusToggle, styles[STATUS_CLASS[task.status]])}
              aria-label={`סטטוס: ${STATUS_LABELS[task.status]}`}
              title={STATUS_LABELS[task.status]}
              onClick={(event) => {
                event.stopPropagation();
                const order = TASK_STATUSES;
                const idx = order.indexOf(task.status);
                const next = order[(idx + 1) % order.length] ?? 'todo';
                onChangeStatus(task.id, next);
              }}
            >
              <Icon size={14} aria-hidden="true" />
            </button>
            <span className={styles.taskCell}>
              <strong>{task.title}</strong>
              {task.description ? (
                <small>{task.description.length > 80 ? task.description.slice(0, 80) + '…' : task.description}</small>
              ) : null}
            </span>
            <span>{projectName}</span>
            <span className={cn(styles.statusBadge, styles[STATUS_CLASS[task.status]])}>
              {STATUS_LABELS[task.status]}
            </span>
            <span className={cn(styles.dateCell, overdue && styles.overdue)}>
              <span className="num">{formatDate(task.due_date)}</span>
            </span>
            <span aria-hidden="true" />
          </div>
        );
      })}
    </div>
  );
}
